import axios from 'axios'
import jwt_decode from 'jwt-decode'
import { inject } from 'mobx-react'
import pubsub from 'pubsub-js'
import { Component } from 'react'
import { Link, withRouter } from 'react-router-dom'
import { Badge, Collapse } from 'reactstrap'
import { HAJKO_FEST_URL, TICKETING_API, TICKETING_APP } from '../../utils/consts'
import unifiedSocketClient from '../../utils/unifiedSocketClient'
import ToggleState from '../Common/ToggleState'
import { getRoutesData } from './getRoutesData.js'
import SidebarRun from './Sidebar.run'
import SidebarUserBlock from './SidebarUserBlock'

/** Normal items for the sidebar */
const SidebarItem = ({ item, isActive, ticketCount }) => (
  <ToggleState state='aside-toggled' nopersist={true}>
    <li className={isActive ? 'active' : ''}>
      {/* <Link
        to={
          item.type === 'externalLink'
            ? {
                pathname: item.path
              }
            : item.path
        }
        target={item.type === 'externalLink' ? '_blank' : '_self'}
        title={item.name}
      >
        {item.label && (
          <Badge tag='div' className='float-right' color={item.label.color}>
            {item.label.value}
          </Badge>
        )}
        {item.path === TICKETING_APP && ticketCount > 0 && (
          <Badge tag='div' className='float-right' color='danger'>
            {ticketCount}
          </Badge>
        )}
        {item.icon && <em className={item.icon} />}
        <span>{item.name}</span>
      </Link> */}
      {item.type === 'externalLink' ? (
        <a href={item.path} target='_self' rel='noopener noreferrer' title={item.name}>
          {item.label && (
            <Badge tag='div' className='float-right' color={item.label.color}>
              {item.label.value}
            </Badge>
          )}
          {item.path === TICKETING_APP && ticketCount > 0 && (
            <Badge tag='div' className='float-right' color='danger'>
              {ticketCount}
            </Badge>
          )}
          {item.icon && <em className={item.icon} />}
          <span>{item.name}</span>
        </a>
      ) : (
        <Link to={item.path} target='_self' title={item.name}>
          {item.label && (
            <Badge tag='div' className='float-right' color={item.label.color}>
              {item.label.value}
            </Badge>
          )}
          {item.path === TICKETING_APP && ticketCount > 0 && (
            <Badge tag='div' className='float-right' color='danger'>
              {ticketCount}
            </Badge>
          )}
          {item.icon && <em className={item.icon} />}
          <span>{item.name}</span>
        </Link>
      )}
    </li>
  </ToggleState>
)

/** Build a sub menu with items inside and attach collapse behavior */
const SidebarSubItem = ({ item, handler, children, isOpen }) => (
  <li>
    <div className='nav-item' onClick={handler}>
      {item.label && (
        <Badge tag='div' className='float-right' color={item.label.color}>
          {item.label.value}
        </Badge>
      )}
      {item.icon && <em className={item.icon} />}
      <span>{item.name}</span>
    </div>
    <Collapse isOpen={isOpen}>
      <ul id={item.path} className='sidebar-nav sidebar-subnav'>
        {children}
      </ul>
    </Collapse>
  </li>
)

@inject('userStore')
class Sidebar extends Component {
  state = {
    collapse: {},
    ticketCount: 0
  }

  componentDidMount() {
    // pass navigator to access router api
    SidebarRun(this.navigator.bind(this))
    pubsub.subscribe('goToProfile', () => {
      this.props.history.push('/profile')
    })
    pubsub.subscribe('goToLogin', () => {
      this.props.history.push('/login')
    })

    if (!this.props.userStore.isAuthenticated) {
      this.props.history.push('/login')
    }

    // Listen for ticket events using the unified socket client
    // The connection is already established in Dashboard.js
    // Store cleanup functions for event listeners
    this.cleanupTicketCreated = unifiedSocketClient.on('ticket-created', () => {
      console.log('📨 Sidebar: ticket-created event received')
      this.fetchTicketCount()
    })

    this.cleanupTicketDeleted = unifiedSocketClient.on('ticket-deleted', () => {
      console.log('📨 Sidebar: ticket-deleted event received')
      this.fetchTicketCount()
    })

    this.cleanupTicketStatusChanged = unifiedSocketClient.on('ticket-status-changed', () => {
      console.log('📨 Sidebar: ticket-status-changed event received')
      this.fetchTicketCount()
    })

    // Initial fetch of ticket count
    this.fetchTicketCount()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.setState({ collapse: {} })
    }
  }

  componentWillUnmount() {
    // Clean up event listeners (remove listeners, don't disconnect socket)
    if (this.cleanupTicketCreated) {
      this.cleanupTicketCreated()
    }
    if (this.cleanupTicketDeleted) {
      this.cleanupTicketDeleted()
    }
    if (this.cleanupTicketStatusChanged) {
      this.cleanupTicketStatusChanged()
    }
    // Don't disconnect the socket here - Dashboard.js manages the connection lifecycle
  }

  fetchTicketCount = async () => {
    try {
      const response = await axios.get(`${TICKETING_API}/tickets/count`)
      this.setState({ ticketCount: response.data.count })
    } catch (error) {
      console.error('Error fetching ticket count:', error)
    }
  }

  navigator(route) {
    this.props.history.push(route)
  }

  routeActive(paths) {
    paths = Array.isArray(paths) ? paths : [paths]
    const currentPath = this.props.location.pathname
    return paths.some(path => currentPath === path || currentPath.startsWith(path + '/'))
  }

  toggleItemCollapse(stateName) {
    for (let c in this.state.collapse) {
      if (this.state.collapse[c] === true && c !== stateName)
        this.setState({
          collapse: {
            [c]: false
          }
        })
    }
    this.setState({
      collapse: {
        [stateName]: !this.state.collapse[stateName]
      }
    })
  }

  getSubRoutes = (item) => item.submenu.map(({ path }) => path)

  render() {
    const token = localStorage.getItem('token')
    const decodedToken = jwt_decode(token)
    const { user_level, permissions_keys } = decodedToken
    const routes = getRoutesData()
    const hasPayrollAccess = permissions_keys?.includes('payroll_access')
    const hasWorkforceManagerAccess = permissions_keys?.includes('has_workforce_manager_access')

    return (
      <aside className='aside-container sticky-left'>
        {/* START Sidebar (left) */}
        <div className='aside-inner'>
          <nav data-sidebar-anyclick-close='' className='sidebar'>
            {/* START sidebar nav */}
            <ul className='sidebar-nav'>
              {/* START user info */}
              <li className='has-user-block'>
                <SidebarUserBlock />
              </li>
              {/* END user info */}

              {routes.map((item, i) => {
                if (item?.icon) {
                  // Special case: Hide "Konfigurimet" for payroll_access users
                  if (item.name === 'Konfigurimet' && hasPayrollAccess) {
                    return null
                  }

                  // Hide "Përdoruesit" for users with has_workforce_manager_access

                  if (item?.allowed_user_levels.includes(user_level) || item.allowed_condition) {
                    if (item?.submenu) {
                      return [
                        <SidebarSubItem
                          item={item}
                          isOpen={this.state.collapse[item.name] || this.routeActive(this.getSubRoutes(item))}
                          handler={this.toggleItemCollapse.bind(this, item.name)}
                          isActive={this.routeActive(this.getSubRoutes(item))}
                          key={i}
                        >
                          <li className='sidebar-subnav-header'>{item.name}</li>
                          {item.submenu
                            .filter((subitem) => subitem.allowed_user_levels.includes(user_level) || subitem.allowed_condition)
                            .map((subitem, i) => (
                              <SidebarItem
                                key={i}
                                item={subitem}
                                isActive={this.routeActive(subitem.path)}
                                ticketCount={this.state.ticketCount}
                              />
                            ))}
                        </SidebarSubItem>
                      ]
                    } else {
                      return <SidebarItem isActive={this.routeActive(item.path)} item={item} key={i} ticketCount={this.state.ticketCount} />
                    }
                  }
                }
              })}
            </ul>
          </nav>
        </div>
      </aside>
    )
  }
}

export default withRouter(Sidebar)
